import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useV3Api } from './api/useV3Api';
import { errorText } from './api/errorText';

export const GLOBAL = '*';
const KEY = 'v3Account';
const stored = () => { try { return sessionStorage.getItem(KEY); } catch (ex) { return null; } };
const remember = (code) => { try { sessionStorage.setItem(KEY, code); } catch (ex) { /* not remembered */ } };

// The accounts the person may see and the one chosen, shared by the V3 pages: from ?account= in the address, else the last one chosen,
// else the first. With withGlobal the first choice is "Global (all customers)" (the account *), the layer every customer inherits.
export function useV3Account({ withGlobal = false, globalLabel = 'Global (all customers)' } = {}) {
  const { get } = useV3Api();
  const [params, setParams] = useSearchParams();
  const wanted = useRef(params.get('account') || stored());
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    get('v3/accounts')
      .then((body) => {
        if (cancelled) return;
        const list = ((body && body.items) || []).slice().sort((a, b) => a.code.localeCompare(b.code));
        const all = withGlobal ? [{ code: GLOBAL, name: globalLabel, active: true }, ...list] : list;
        setAccounts(all);
        setAccount(all.find((a) => a.code === wanted.current) || all[0] || null);
        setLoading(false);
      })
      .catch((ex) => { if (!cancelled) { setError(errorText(ex)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [get, withGlobal, globalLabel]);

  const choose = (code) => {
    const found = accounts.find((a) => a.code === code);
    if (!found) return;
    setAccount(found);
    remember(found.code);
    if (params.has('account')) {
      const next = new URLSearchParams(params);
      next.delete('account');
      setParams(next, { replace: true });
    }
  };

  return { accounts, account, choose, loading, error };
}
